import { ElementControllerHost } from "../shared/core/controllers/ElementControllerHost";
import { FrameController } from "../shared/core/controllers/FrameController";
import type { HomepageAudio } from "./Audio";
import { requiredChild, requiredElement } from "./Dom";
import type { LogoKey } from "./Types";
import { prefersReducedMotion } from "../shared/Accessibility";
import { createSmashKeyboard } from "./SmashKeyboard";
import { prepareSmashDebris } from "./SmashDebris";

export function createEasterEggHandler(
	audio: HomepageAudio,
): (key: LogoKey) => void {
	let smashing = false;

	function smash(power: number): void {
		if (!("Matter" in window)) {
			return;
		}
		const matter = window.Matter;

		if (prefersReducedMotion()) {
			audio.playEffect("boom.mp3");
			smashing = false;
			return;
		}
		const { Engine } = matter;
		const engine = Engine.create({
			gravity: { x: 0, y: 0.9 },
			velocityIterations: 1,
			positionIterations: 1,
			constraintIterations: 12,
		});

		const {
			draw: drawKeyboard,
			rememberPrevious,
			releaseHAtTrough,
		} = createSmashKeyboard(matter, engine, power);
		drawKeyboard();

		const frames = new FrameController(
			new ElementControllerHost(document.body),
		);
		const prepared = prepareSmashDebris(frames.timeline, power);
		const homepage = requiredChild<HTMLElement>(document, ".homepage");
		for (const child of homepage.children) {
			child.setAttribute("inert", "");
		}
		let elapsed = 0;
		const step = 1000 / 60;
		frames.addEventListener("update", (event) => {
			// SAFETY: this handler receives only FrameController's update events.
			elapsed += Math.min(
				(event as CustomEvent<{ deltaMs: number }>).detail.deltaMs,
				1000 / 30,
			);
			while (elapsed >= step) {
				rememberPrevious();
				Engine.update(engine, step);
				releaseHAtTrough();
				elapsed -= step;
			}
		});
		// Draw between fixed physics steps so high-refresh screens don't repeat every other pose.
		frames.addEventListener("draw", () => drawKeyboard(elapsed / step));

		void prepared.then(() => {
			audio.playEffect("boom.mp3");
			frames.timeline.resume();
			frames.timeline.setTimeout(() => {
				const message = document.createElement("div");
				message.className = "smash-refresh-message";
				message.setAttribute("role", "status");
				const heading = document.createElement("h1");
				heading.append(
					"You're going to need to refresh the page.",
					document.createElement("br"),
					"Soz.",
				);
				message.append(heading);
				homepage.append(message);
			}, 5000);
		});
	}

	function triggerSmash(power: number): void {
		if (smashing) {
			return;
		}
		smashing = true;
		if ("Matter" in window) {
			smash(power);
			return;
		}

		const script = document.createElement("script");
		script.src = "/js/build/homepage/matter.js";
		script.addEventListener("load", () => smash(power), { once: true });
		script.addEventListener(
			"error",
			() => {
				smashing = false;
			},
			{ once: true },
		);
		document.head.append(script);
	}

	const actions = {
		MOO: () => {
			requiredElement<HTMLElement>("hi").textContent = "🐮 Moo.";
		},
		WHAM: () => audio.playEffect("wham.mp3"),
		SHAMWOW: () => {
			window.location.href = "https://www.youtube.com/watch?v=1Q39yGLPkMY";
		},
		MOW: () => {
			requiredElement<HTMLElement>("hi").textContent = "🐱 Meow.";
			audio.playEffect("mow.mp3");
		},
	};

	let typed = "";
	return (key) => {
		typed = (typed + key).slice(-24);
		const smashCommand = /SMA{1,20}SH$/.exec(typed);
		if (smashCommand) {
			triggerSmash(smashCommand[0].length - 4);
			typed = "";
			return;
		}
		for (const [word, action] of Object.entries(actions)) {
			if (!typed.endsWith(word)) {
				continue;
			}
			action();
			typed = "";
			return;
		}
	};
}
